const updateCalculatedColumns = (calcCols, removeCols) => {
	// 1) Snapshot originals
	const originals = (Array.isArray(calcCols) ? calcCols : []).map((col) => ({
		originalField: col.field,
		originalFields: Array.isArray(col.fields) ? col.fields.slice() : [],
		originalExpr: typeof col.expression === 'string' ? col.expression : '',
	}));

	// 2) Build initial drop set from the removeCols array
	const dropSet = new Set(Array.isArray(removeCols) ? removeCols : []);

	// 3) Cascade‑drop any calc that refers to a dropped field
	let changed;
	do {
		changed = false;
		for (const col of originals) {
			if (!dropSet.has(col.originalField) && col.originalFields.some((f) => dropSet.has(f))) {
				dropSet.add(col.originalField);
				changed = true;
			}
		}
	} while (changed);

	// 4) Identify removed columns
	const removed = originals.filter((col) => dropSet.has(col.originalField)).map((col) => col.originalField);

	// 5) Keep the rest
	const kept = originals.filter((col) => !dropSet.has(col.originalField));

	// 6) Build updated columns (no renames needed, just pass through)
	const updated = kept.map((col) => ({
		field: col.originalField,
		fields: col.originalFields.slice(),
		expression: col.originalExpr,
	}));

	// 7) Always return arrays
	return {
		updated,
		removed,
	};
};

const removeDatasetInDataSpace = (dataSpace, datasetInstanceId) => {
	const updatedDataSpace = {};

	// Handle the DataSpace _id
	updatedDataSpace._id = dataSpace._id;

	// Handle the DataSpace creationDate
	updatedDataSpace.creationDate = dataSpace.creationDate;

	// Handle the DataSpace lastRefreshedDate
	updatedDataSpace.lastRefreshedDate = dataSpace.lastRefreshedDate;

	// Handle the DataSpace name
	updatedDataSpace.name = dataSpace.name;

	// Handle the DataSpace createdBy
	updatedDataSpace.createdBy = dataSpace.createdBy;

	// Handle the DataSpace isPublic
	updatedDataSpace.isPublic = dataSpace.isPublic;

	// Handle the DataSpace isCurrentViewSaved
	updatedDataSpace.isCurrentViewSaved = dataSpace.isCurrentViewSaved;

	// Handle the DataSpace isFeatured
	updatedDataSpace.isFeatured = dataSpace.isFeatured;

	const datasetInstance = dataSpace.datasets.find((dataset) => dataset._id === datasetInstanceId);

	const columnsToBeRemoved = datasetInstance.fields.map((field) => field.name);

	// calculatedColumns: If a column is removed that is referenced in a calculated column, remove the calculated column
	// Also remove caulculated columns that refer to calculated columns that were removed
	const { updated: updatedCalculatedColumns, removed: removedCalculatedColumns } = updateCalculatedColumns(
		dataSpace.calculatedColumns || [],
		columnsToBeRemoved,
	);

	// Update the calculated columns in the dataSpace
	updatedDataSpace.calculatedColumns = updatedCalculatedColumns;

	// Add the removed calculated columns to the columnsToBeRemoved array
	columnsToBeRemoved.push(...removedCalculatedColumns);

	// headerNamesState: If a column is removed that is referenced in headerNamesState, we just remove that header
	updatedDataSpace.headerNamesState = dataSpace.headerNamesState.filter(
		(header) => !columnsToBeRemoved.includes(header.field),
	);

	// datasets: We just replace the dataset with the new dataset
	updatedDataSpace.datasets = dataSpace.datasets.filter((dataset) => dataset._id !== datasetInstanceId);

	// frozenColumns: If a frozen column references a column that is removed, we remove the frozen column
	updatedDataSpace.frozenColumns = dataSpace.frozenColumns.filter(
		(frozenColumn) => !columnsToBeRemoved.includes(frozenColumn),
	);

	// toggledColumnsState: If a toggled column references a column that is removed, we remove the toggled column
	updatedDataSpace.toggledColumnsState = dataSpace.toggledColumnsState.filter(
		(toggledColumn) => !columnsToBeRemoved.includes(toggledColumn),
	);

	// columnMetadata: Remove any columns that were removed
	updatedDataSpace.columnMetadata = dataSpace.columnMetadata.filter(
		(column) => !columnsToBeRemoved.includes(column.field),
	);

	// joinData: Remove the removed dataset and any joins that referenced it
	const updatedJoinData = [];
	for (let joinsDataset of dataSpace.joinData) {
		// If the dataset is the one being removed, we don't need to add it to the updatedJoinData
		if (joinsDataset._id === datasetInstanceId) {
			continue;
		}

		let updatedDatasetJoins = [];
		// Populate updatedDatasetJoins with the updated joins of the dataset
		for (let join of joinsDataset.joins) {
			// If one of the join fields belongs to the removed dataset, remove the join
			if (
				columnsToBeRemoved.includes(join.primaryDataset.joinFieldName) ||
				columnsToBeRemoved.includes(join.foreignDataset.joinFieldName)
			) {
				continue;
			}
			// Otherwise, push the join
			updatedDatasetJoins.push(join);
		}

		updatedJoinData.push({
			...joinsDataset,
			joins: updatedDatasetJoins,
		});
	}

	// If the anchor dataset was deleted, assign a different dataset as the anchor (if available)
	if (!dataSpace.joinData.some((joinDataset) => joinDataset.isAnchor)) {
		if (updatedJoinData.length > 0) {
			updatedJoinData[0].isAnchor = true;
		}
	}

	updatedDataSpace.joinData = updatedJoinData;

	// views: Remove any views that reference the removed column
	updatedDataSpace.views = dataSpace.views.filter((view) => {
		// Check the internal state sort field
		if (view.internalProperties.state.sortField) {
			if (columnsToBeRemoved.includes(view.internalProperties.state.sortField)) {
				return false;
			}
		}
		// Check the internal state multiSortMeta field
		if (view.internalProperties.state.multiSortMeta) {
			for (let sort of view.internalProperties.state.multiSortMeta) {
				if (columnsToBeRemoved.includes(sort.field)) {
					return false;
				}
			}
		}
		// Check the internal state filters field
		if (view.internalProperties.state.filters) {
			for (let columnName in view.internalProperties.state.filters) {
				if (columnsToBeRemoved.includes(columnName)) {
					return false;
				}
			}
		}
		// Check the internal state columnOrder field
		if (view.internalProperties.state.columnOrder) {
			for (let column of view.internalProperties.state.columnOrder) {
				if (columnsToBeRemoved.includes(column)) {
					return false;
				}
			}
		}
		// Check the externalProperties selectedColumns
		if (view.externalProperties.selectedColumns) {
			for (let selectedColumn of view.externalProperties.selectedColumns) {
				if (columnsToBeRemoved.includes(selectedColumn.field)) {
					return false;
				}
			}
		}
		// Check the externalProperties filterStates
		if (view.externalProperties.filterStates) {
			for (let columnName in view.externalProperties.filterStates) {
				if (columnsToBeRemoved.includes(columnName)) {
					return false;
				}
			}
		}
		// Check the externalProperties frozenColumns
		if (view.externalProperties.frozenColumns) {
			for (let frozenColumn of view.externalProperties.frozenColumns) {
				if (columnsToBeRemoved.includes(frozenColumn)) {
					return false;
				}
			}
		}
		return true;
	});

	// internalState: Set to an empty object
	updatedDataSpace.internalState = {};

	return updatedDataSpace;
};

export default removeDatasetInDataSpace;
